import { useEffect, useState, useRef } from "react";
import axios from "axios";
import "../css/removeDuplicateChemist.css";
import { useLocation, useNavigate } from "react-router-dom";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import formatDate from "../utils/formatDate";
import CustomButton from "./CustomButton";
import Loader from "../dist/Loader";

const RemoveDuplicateChemistPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromEditPage = location.state?.fromEdit === true;
  const shouldRefresh = location.state?.shouldRefresh === true;

  const [states, setStates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [sortOption, setSortOption] = useState("");
  const [selectedSort, setSelectedSort] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [chemistResults, setChemistResults] = useState([]);
  const [doctorResults, setDoctorResults] = useState([]);
  const [errors, setErrors] = useState({});
  const [selectedEmployeeName, setSelectedEmployeeName] = useState("");
  const [selectedEntities, setSelectedEntities] = useState([]);
  const [showChemists, setShowChemists] = useState(false);
  const [showDoctors, setShowDoctors] = useState(false);
  const [loading, setLoading] = useState(false);
  const tableRef = useRef(null);

  const sortOptions = [
    { label: "Name", value: "name" },
    { label: "Ascending Route", value: "route" },
    { label: "Ascending Area", value: "area" },
    { label: "Ascending Visits", value: "asc_visits" },
    { label: "Descending Visits", value: "desc_visits" },
    { label: "Ascending POB", value: "asc_pob" },
    { label: "Descending POB", value: "desc_pob" },
    { label: "Latest Visit", value: "latest_visit" },
    { label: "Latest POB Date", value: "latest_pob_date" },
  ];

  const updateChemistStatus = async (chemist_code, status) => {
    try {
      const url = `${BASE_URL}/api/chemist/chemist/${chemist_code}/status`;
      const res = await axios.put(
        url,
        { is_active: status },
        { headers: getAuthHeaders() },
      );
      return res.data;
    } catch (error) {
      alert("Error updating chemist status");
      return null;
    }
  };

  const updateDoctorStatus = async (doctor_code, status) => {
    try {
      const url = `${BASE_URL}/api/doctor/${doctor_code}/status`;
      const res = await axios.put(
        url,
        { is_active: status },
        { headers: getAuthHeaders() },
      );
      return res.data;
    } catch (error) {
      alert("Error updating doctor status");
      return null;
    }
  };

  useEffect(() => {
    fetch(`${BASE_URL}/api/states`, {
      headers: getAuthHeaders(),
    })
      .then((res) => res.json())
      .then((data) => setStates(data))
      .catch((err) => console.error("State fetch error:", err));
  }, []);

  useEffect(() => {
    if (selectedState) {
      axios
        .get(`${BASE_URL}/api/employees?state_id=${selectedState}`, {
          headers: getAuthHeaders(),
        })
        .then((res) => setEmployees(res.data))
        .catch((err) => console.error(err));
    } else {
      setEmployees([]);
    }
  }, [selectedState]);

  useEffect(() => {
    if (fromEditPage && location.state?.filters) {
      const {
        selectedState,
        selectedEmployee,
        sortOption,
        showChemists,
        showDoctors,
      } = location.state.filters;
      setSelectedState(selectedState);
      setSelectedEmployee(selectedEmployee);
      setSortOption(sortOption);
      setSelectedSort(sortOption);
      setShowChemists(showChemists);
      setShowDoctors(showDoctors);
      setSelectedEmployeeName(location.state.selectedEmployeeName || "");
      setShowResults(true);

      if (shouldRefresh) {
        if (showChemists) {
          fetchChemistData(selectedEmployee, selectedState, sortOption);
        }
        if (showDoctors) {
          fetchDoctorData(selectedEmployee, selectedState, sortOption);
        }
      }
    }
  }, [fromEditPage, shouldRefresh, location.state]);

  const fetchChemistData = (employeeId, stateId, sortBy) => {
    return axios
      .get(`${BASE_URL}/api/chemist/chemists/filter`, {
        params: {
          state_id: stateId,
          employee_id: employeeId,
          sort_by: sortBy || "area",
          retailer_type: "chemist",
          is_report: true,
        },
        headers: getAuthHeaders(),
      })
      .then((res) => {
        const normalized = res.data.map((c) => ({
          ...c,
          type: "Chemist",
          code: c.chemist_code,
          name: c.chemist_name,
          contact: c.contact_person || "-",
        }));
        setChemistResults(normalized);
      })
      .catch((err) => {
        console.error("Error fetching chemists:", err);
        setChemistResults([]);
      });
  };

  const fetchDoctorData = (employeeId, stateId, sortBy) => {
    return axios
      .get(`${BASE_URL}/api/doctors-filter`, {
        params: {
          state_id: stateId,
          employee_id: employeeId,
          sort_by: sortBy || "name",
          retailer_type: "doctor",
        },
        headers: getAuthHeaders(),
      })
      .then((res) => {
        const normalized = res.data.map((d) => ({
          ...d,
          type: "Doctor",
          code: d.doctor_code,
          name: d.doctor_name,
          contact:
            d.contact_person ||
            d.contact_name ||
            d.contact ||
            d.contactperson ||
            d.cp_name ||
            "-",
        }));
        setDoctorResults(normalized);
      })
      .catch(() => setDoctorResults([]));
  };

  const handleSelectEntity = (id) => {
    setSelectedEntities((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleRemoveSelected = async () => {
    if (selectedEntities.length === 0) {
      alert("Please select at least one to remove");
      return;
    }

    if (showChemists) {
      const updatedChemists = [];
      for (const item of chemistResults) {
        if (selectedEntities.includes(item.id)) {
          const apiResult = await updateChemistStatus(item.chemist_code, false);
          if (!(apiResult && apiResult.is_active === false)) {
            updatedChemists.push(item);
          }
        } else {
          updatedChemists.push(item);
        }
      }
      setChemistResults(updatedChemists);
    }

    if (showDoctors) {
      const updatedDoctors = [];
      for (const item of doctorResults) {
        if (selectedEntities.includes(item.id)) {
          const apiResult = await updateDoctorStatus(item.doctor_code, false);
          if (!(apiResult && apiResult.is_active === false)) {
            updatedDoctors.push(item);
          }
        } else {
          updatedDoctors.push(item);
        }
      }
      setDoctorResults(updatedDoctors);
    }

    setSelectedEntities([]);
  };

  const handleRemoveFromRoute = async (id, code, type) => {
    let success = false;
    if (type === "chemist") {
      const apiResult = await updateChemistStatus(code, false);
      success = apiResult && apiResult.is_active === false;
    } else if (type === "doctor") {
      const apiResult = await updateDoctorStatus(code, false);
      success = apiResult && apiResult.is_active === false;
    }
    if (success) {
      if (type === "chemist") {
        setChemistResults((prev) => prev.filter((item) => item.id !== id));
      } else if (type === "doctor") {
        setDoctorResults((prev) => prev.filter((item) => item.id !== id));
      }
    }
  };
const handleEditView = (code, type) => {
  if (!code || code === "undefined") {
    alert("Invalid code");
    return;
  }

  const currentFilters = {
    selectedState,
    selectedEmployee,
    sortOption: selectedSort,
    showChemists,
    showDoctors,
  };

  // ✅ Navigate immediately — pass only employeeId, edit page fetches its own data
  const stateToPass = {
    filters: currentFilters,
    selectedEmployeeName,
    fromEdit: true,
    employeeId: selectedEmployee,  // ✅ only this needed — no pre-fetch
  };

  if (type === "doctor") {
    navigate(`/edit-doctor?doctor_code=${code}`, { state: stateToPass });
  } else if (type === "chemist") {
    navigate(`/edit-retailer?chemist_code=${code}`, { state: stateToPass });
  }
};
  // const handleEditView = (code, type) => {
  //   if (!code || code === "undefined") {
  //     alert("Invalid code");
  //     return;
  //   }

  //   const currentFilters = {
  //     selectedState,
  //     selectedEmployee,
  //     sortOption: selectedSort,
  //     showChemists,
  //     showDoctors,
  //   };
  //   const stateToPass = {
  //     filters: currentFilters,
  //     selectedEmployeeName,
  //     fromEdit: true,
  //   };

  //   if (type === "doctor") {
  //     navigate(`/edit-doctor?doctor_code=${code}`, { state: stateToPass });
  //   } else if (type === "chemist") {
  //     navigate(`/edit-retailer?chemist_code=${code}`, { state: stateToPass });
  //   } else {
  //     alert("Invalid entity type or code");
  //   }
  // };

  const handleSubmit = async () => {
    const newErrors = {};

    if (!selectedState) newErrors.state = "Required";
    if (!selectedEmployee) newErrors.employee = "Required";
    if (!showChemists && !showDoctors) {
      newErrors.type = "Please select Chemist or Doctor";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length !== 0) {
      setShowResults(false);
      setChemistResults([]);
      setDoctorResults([]);
      setLoading(false);
      return;
    }

    const emp = employees.find((e) => e.id == selectedEmployee);
    if (emp) {
      setSelectedEmployeeName(
        `${emp.first_name} ${emp.last_name} (${emp.designation}|${emp.headquarter_name}|${emp.division}|${emp.state_name})`,
      );
    }

    setShowResults(true);
    setSelectedEntities([]);
    setLoading(true);

    setChemistResults([]);
    setDoctorResults([]);

    try {
      const promises = [];

      if (showChemists) {
        promises.push(
          fetchChemistData(selectedEmployee, selectedState, selectedSort),
        );
      }

      if (showDoctors) {
        promises.push(
          fetchDoctorData(selectedEmployee, selectedState, selectedSort),
        );
      }

      await Promise.all(promises);
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  };
  const handlePrint = () => {
    if (!tableRef.current) return;

    const printContent = tableRef.current.outerHTML;

    const printWindow = window.open("", "", "width=900,height=700");

    printWindow.document.write(`
    <html>
      <head>
        <title>Print Table</title>
        <style>
          body { font-family: Arial; padding:20px; }
          table { border-collapse: collapse; width:100%; }
          th, td { border:1px solid #ccc; padding:8px; text-align:left; }
          th { background:#f3f4f6; }
        </style>
      </head>
      <body>
        ${printContent}
      </body>
    </html>
  `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };
  const handleExportExcel = () => {
    if (!tableRef.current) return;

    const rows = Array.from(tableRef.current.querySelectorAll("tr"));

    const csv = rows.map((row) => {
      const cols = Array.from(row.querySelectorAll("th,td"));
      return cols
        .map((col) => `"${col.innerText.replace(/"/g, '""')}"`)
        .join(",");
    });

    const csvContent = csv.join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "duplicate_chemist_report.csv";
    link.click();
  };

  return (
    <div className="remove-duplicate-chemist-main-container">
      <div className="p-4 border-2 border-gray-300 rounded-lg bg-white w-full">
        <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-6">
          <span className="text-blue-700">Remove </span>
          <span className="text-blue-500">Duplicate</span>
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-10 justify-center mx-auto w-fit">
          {/* State */}
          <div className="flex items-center gap-4">
            <label className="font-semibold w-28 text-right">State</label>
            <select
              className="w-80 border border-gray-400 rounded-md h-10 px-2"
              value={selectedState}
              onChange={(e) => {
                setSelectedState(e.target.value);
                setSelectedEmployee("");
                setShowResults(false);
              }}
            >
              <option value="">Select State</option>
              {states.map((state) => (
                <option key={state.id} value={state.id}>
                  {state.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-4">
            <label className="font-semibold w-28 text-right">Employee</label>
            <select
              className="w-80 border border-gray-400 rounded-md h-10 px-2"
              value={selectedEmployee}
              onChange={(e) => {
                setSelectedEmployee(e.target.value);
                setShowResults(false);
              }}
            >
              <option value="">Select Employee</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.first_name} {emp.last_name} | {emp.employee} ({emp.designation}|
                  {emp.headquarter_name}|{emp.division}|{emp.state_name})
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-4">
            <label className="font-semibold w-28 text-right">Sort By</label>
            <select
              className="w-80 border border-gray-400 rounded-md h-10 px-2"
              value={selectedSort}
              onChange={(e) => {
                setSelectedSort(e.target.value);
                setSortOption(e.target.value);
                setShowResults(false);
              }}
            >
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-10 pl-32">
            <label className="flex items-center gap-2 font-semibold">
              Chemist/Retailer
              <input
                type="checkbox"
                className="w-4 h-4"
                checked={showChemists}
                onChange={() => {
                  setShowChemists(!showChemists);
                  setShowResults(false);
                }}
              />
            </label>

            <label className="flex items-center gap-2 font-semibold">
              Doctor
              <input
                type="checkbox"
                className="w-4 h-4"
                checked={showDoctors}
                onChange={() => {
                  setShowDoctors(!showDoctors);
                  setShowResults(false);
                }}
              />
            </label>
          </div>
        </div>

        {/* Button */}
        <div className="flex justify-center mt-10">
          <button
            className="bg-green-600 hover:bg-green-700 text-white px-8 py-2 rounded-md font-semibold"
            onClick={handleSubmit}
          >
            Go
          </button>
        </div>
      </div>

      {showResults && (
        <div className="results-container">
          {loading && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
              <Loader onCancel={() => setLoading(false)} />
            </div>
          )}
          {(showChemists && chemistResults.length > 0) ||
          (showDoctors && doctorResults.length > 0) ? (
            <div className="combined-results-section mb-10">
              <div className="border rounded shadow  table-wrapper">
                <table
                  ref={tableRef}
                  className="unlock-dcr-leave-table text-sm "
                >
                  <thead className="bg-blue-500 text-white">
                    <tr className="bg-cyan-50">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-cyan-800 uppercase tracking-wider border-b border-cyan-200">
                        S.No
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-cyan-800 uppercase tracking-wider border-b border-cyan-200">
                        Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-cyan-800 uppercase tracking-wider border-b border-cyan-200">
                        Code
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-cyan-800 uppercase tracking-wider border-b border-cyan-200">
                        Name
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-cyan-800 uppercase tracking-wider border-b border-cyan-200">
                        Contact Person
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-cyan-800 uppercase tracking-wider border-b border-cyan-200">
                        City
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-cyan-800 uppercase tracking-wider border-b border-cyan-200">
                        Area
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-cyan-800 uppercase tracking-wider border-b border-cyan-200">
                        Route
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-cyan-800 uppercase tracking-wider border-b border-cyan-200">
                        Mobile
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-cyan-800 uppercase tracking-wider border-b border-cyan-200">
                        Address
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-cyan-800 uppercase tracking-wider border-b border-cyan-200">
                        View/Edit
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-cyan-800 uppercase tracking-wider border-b border-cyan-200">
                        Total POB
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-cyan-800 uppercase tracking-wider border-b border-cyan-200">
                        Total Visits
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-cyan-800 uppercase tracking-wider border-b border-cyan-200">
                        Latest Visit
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-cyan-800 uppercase tracking-wider border-b border-cyan-200">
                        Latest POB Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-cyan-800 uppercase tracking-wider border-b border-cyan-200">
                        Remove
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-cyan-800 uppercase tracking-wider border-b border-cyan-200">
                        <button
                          className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-1 rounded text-xs font-semibold transition-colors"
                          onClick={handleRemoveSelected}
                        >
                          Remove Selected
                        </button>
                      </th>
                    </tr>
                  </thead>

                  <tbody className="bg-white divide-y divide-gray-200">
                    {/* Chemist Results */}
                    {showChemists &&
                      chemistResults.map((item, index) => (
                        <tr
                          key={`chemist-${item.id}`}
                          className={`${
                            selectedEntities.includes(item.id)
                              ? "!bg-orange-500 text-white"
                              : index % 2 === 0
                                ? "bg-white"
                                : "bg-gray-50"
                          } hover:bg-gray-100`}
                        >
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                            {index + 1}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                            Chemist
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                            {item.chemist_code}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 fixed-address">
                            {item.chemist_name}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 fixed-address">
                            {item.contact || "-"}
                          </td>

                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                            {item.city_name}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                            {item.area_name}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 fixed-address">
                            {item.route_name}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                            {item.phone || item.mobile}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 truncate max-w-xs fixed-address">
                            {item.address}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                            <button
                              onClick={() =>
                                handleEditView(item.chemist_code, "chemist")
                              }
                              className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-xs font-semibold transition-colors"
                            >
                              Edit/View
                            </button>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                            {item.totalPOB || item.total_pob}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                            {item.totalVisits || item.total_visits}
                          </td>

                          {/* latest visit and latest_pob_date formatted */}
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                            {formatDate(item.latest_visit)}
                          </td>

                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                            {formatDate(
                              item.latestPobDate || item.latest_pob_date,
                            )}
                          </td>

                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                            <button
                              onClick={() =>
                                handleRemoveFromRoute(
                                  item.id,
                                  item.chemist_code,
                                  "chemist",
                                )
                              }
                              className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-xs font-semibold transition-colors"
                            >
                              Remove
                            </button>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={selectedEntities.includes(item.id)}
                              onChange={() => handleSelectEntity(item.id)}
                              className="h-4 w-4 text-green-500 focus:ring-green-500 border-gray-300 rounded"
                            />
                          </td>
                        </tr>
                      ))}

                    {/* Doctor Results */}
                    {showDoctors &&
                      doctorResults.map((item, index) => (
                        <tr
                          key={`doctor-${item.id}`}
                          className={`${
                            selectedEntities.includes(item.id)
                              ? "!bg-orange-500 text-white"
                              : (chemistResults.length + index) % 2 === 0
                                ? "bg-white"
                                : "bg-gray-50"
                          } hover:bg-gray-100`}
                        >
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                            {chemistResults.length + index + 1}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                            Doctor
                          </td>

                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                            {item.doctor_code}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                            {item.doctor_name}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                            {item.contact || "-"}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                            {item.city_name}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                            {item.area_name}
                          </td>
                          <td className="px-4 py-3 whitespace-normal text-sm text-gray-700">
                            {item.route_name}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                            {item.phone || item.mobile}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 truncate max-w-xs">
                            {item.address}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                            <button
                              onClick={() =>
                                handleEditView(item.doctor_code, "doctor")
                              }
                              className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-xs font-semibold transition-colors"
                            >
                              Edit/View
                            </button>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                            {item.totalPOB || item.total_pob}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                            {item.totalVisits || item.total_visits}
                          </td>

                          {/* both fields formatted with formatDate */}
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                            {formatDate(item.latest_visit)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                            {formatDate(
                              item.latestPobDate || item.latest_pob_date,
                            )}
                          </td>

                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                            <button
                              onClick={() =>
                                handleRemoveFromRoute(
                                  item.id,
                                  item.doctor_code,
                                  "doctor",
                                )
                              }
                              className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-xs font-semibold transition-colors"
                            >
                              Remove
                            </button>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={selectedEntities.includes(item.id)}
                              onChange={() => handleSelectEntity(item.id)}
                              className="h-4 w-4 text-green-500 focus:ring-green-500 border-gray-300 rounded"
                            />
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 flex gap-4 justify-center">
                <CustomButton
                  className="whitespace-nowrap"
                  onClick={handlePrint}
                  variant="print"
                >
                  🖨 Print
                </CustomButton>
                <CustomButton
                  className="whitespace-nowrap"
                  onClick={handleExportExcel}
                  variant="export"
                >
                  📥 Export
                </CustomButton>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};
export default RemoveDuplicateChemistPage;
